import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  // Send the httpOnly auth cookies on every cross-origin request. The backend
  // CORS list is exact + credentials:true, so this is safe.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── In-memory access token ────────────────────────────────────────────────────────────────────────
// The access token is NEVER persisted to localStorage. It lives in this module
// variable and is rehydrated on app load via /auth/refresh (from the httpOnly
// cookie). We still send it as an Authorization: Bearer header so the app works
// even against a backend/browser combo where the cookie isn't flowing yet — this
// is the no-lockout bridge during rollout.
let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => { accessToken = t; };
export const getAccessToken = (): string | null => accessToken;

/** Read a non-httpOnly cookie (used only for the readable csrf_token). */
function readCookie(name: string): string | null {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

/** The readable CSRF double-submit token (from the non-httpOnly csrf_token cookie). */
export const getCsrfToken = (): string | null => readCookie('csrf_token');

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

// Request interceptor: attach the in-memory Bearer token + CSRF header (mutations).
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // CSRF double-submit: echo the readable csrf_token cookie on mutating methods.
    const method = (config.method || 'get').toLowerCase();
    if (MUTATING.has(method)) {
      const csrf = readCookie('csrf_token');
      if (csrf) config.headers['X-CSRF-Token'] = csrf;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling.
// On 401 from ANY endpoint (token expired/invalid), trigger a SOFT logout:
//   - Clear localStorage
//   - Dispatch 'orbit:auth-expired' event so AuthContext can React-route to /login
//   - Never hard-reload via window.location.href (that destroyed in-flight uploads
//     and any unsaved form state — the original "logged out mid-upload" report).
//
// Distinction from before: the OLD code did `window.location.href = '/login'` which
// tore down everything. The NEW code uses a custom event the AuthContext listens for,
// so the React tree gracefully unmounts and shows the login page.
// In-flight GET coalescing map (see the wrapper below). No stored/TTL cache — coalescing alone kills the
// mount-burst duplicate GETs and never serves stale data, so there's nothing to invalidate on a write.
const _getInflight = new Map<string, Promise<any>>();

let authExpiredFired = false;

// ── Single-flight token refresh ─────────────────────────────────────────────────────────────────────
// On a 401 we try ONE /auth/refresh (from the httpOnly refresh cookie) and retry
// the original request. Concurrent 401s share the SAME in-flight refresh promise
// so we never fire a storm of refreshes. Raw axios (not `api`) so it doesn't
// recurse through this interceptor; we still forward the in-memory Bearer so it
// also satisfies a backend that authenticates refresh via the access token.
let refreshPromise: Promise<string | null> | null = null;
function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    const headers: Record<string, string> = {};
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true, headers })
      .then((res) => {
        const t = (res as any)?.data?.data?.token || null;
        setAccessToken(t);
        return t;
      })
      .catch(() => { setAccessToken(null); return null; })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (response) => {
    // End-to-end console trail for regen/creative actions (so every step is visible in the console).
    try {
      const url = response.config?.url || '';
      const method = (response.config?.method || 'get').toUpperCase();
      if (/\/regen\//.test(url) && method !== 'GET') {
        // eslint-disable-next-line no-console
        console.info(`[ORBIT ✓] ${method} ${url}`, response.data?.data?.id || response.data?.job_id || response.data?.success || '');
      }
    } catch { /* noop */ }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    // 429 (rate-limited) → back off and RETRY (up to 2×) instead of surfacing the error. Honors the
    // server's Retry-After when present, else exponential backoff with jitter. This is what turned a
    // burst of parallel page-load requests into a wall of red 429s — now they queue and settle.
    const rcfg: any = error?.config;
    if (status === 429 && rcfg && !rcfg.__noRetry) {
      rcfg.__retry = (rcfg.__retry || 0) + 1;
      if (rcfg.__retry <= 2) {
        const ra = parseInt(error?.response?.headers?.['retry-after'] || '', 10);
        const wait = (Number.isFinite(ra) ? ra * 1000 : 600 * Math.pow(2, rcfg.__retry)) + Math.floor(Math.random() * 250);
        await new Promise(r => setTimeout(r, wait));
        return api(rcfg);
      }
    }
    // Log EVERY API failure with the endpoint + status + server message → visible in the console.
    try {
      const cfg = error?.config || {};
      // eslint-disable-next-line no-console
      console.error(`[ORBIT ✗] ${(cfg.method || 'get').toUpperCase()} ${cfg.url || ''} → ${status || 'network'}`,
        error?.response?.data?.error || error?.response?.data || error?.message || error);
    } catch { /* noop */ }
    if (status === 401 && rcfg) {
      const url = rcfg.url || '';
      const isAuthEndpoint = /\/auth\/(refresh|login|logout)/.test(url);
      // Try ONE silent refresh + retry before giving up (skip for the auth
      // endpoints themselves and for a request we already retried — loop guard).
      if (!isAuthEndpoint && !rcfg.__isAuthRetry) {
        rcfg.__isAuthRetry = true;
        const newTok = await refreshAccessToken();
        if (newTok) {
          rcfg.headers = rcfg.headers || {};
          rcfg.headers.Authorization = `Bearer ${newTok}`;
          return api(rcfg);
        }
      }
      // Refresh failed (or this was an auth call) → clear session once and let
      // AuthContext route to /login. Token is in memory only; only `user` (a
      // non-credential convenience copy) may linger in localStorage.
      if (!authExpiredFired) {
        authExpiredFired = true;
        setAccessToken(null);
        localStorage.removeItem('user');
        try { window.dispatchEvent(new Event('orbit:auth-expired')); } catch {}
        // Reset the dedup flag after a short delay so a fresh login can be detected later
        setTimeout(() => { authExpiredFired = false; }, 5000);
      }
    }
    return Promise.reject(error);
  }
);

// ── GET in-flight coalescing ──────────────────────────────────────────────────────────────────────
// The 429 storm came from many components firing the SAME GET on mount / every tab switch. Collapse
// concurrent identical GETs onto ONE live request — never stale (it IS the request in flight), and no
// stored cache, so polls/statuses/balances are always fresh. Each caller gets a SHALLOW-CLONED response
// (`{ ...res }` with a cloned `data`) so one component mutating `res.data` can't corrupt the others that
// resolved off the same shared promise.
const _rawGet = api.get.bind(api);
const _clone = (res: any) => (res && typeof res === 'object'
  ? { ...res, data: Array.isArray(res.data) ? [...res.data] : (res.data && typeof res.data === 'object' ? { ...res.data } : res.data) }
  : res);
api.get = ((url: string, config?: any) => {
  const key = url + (config?.params ? JSON.stringify(config.params) : '');
  const inf = _getInflight.get(key);
  if (inf) return inf.then(_clone);
  const p = _rawGet(url, config);
  _getInflight.set(key, p); p.finally(() => _getInflight.delete(key));
  return p.then(_clone);
}) as typeof api.get;

// Auth endpoints
export const verticalsApi = {
  getAll: () => api.get('/verticals'),
};

export const authApi = {
  register: (data: { name: string; email: string; password: string; role?: string }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  logout: () => api.post('/auth/logout'),
  getBuyers: () => api.get('/auth/buyers'),
  getUsers: () => api.get('/auth/users'), // For sharing - accessible by all authenticated users
  getUsersByRole: (role: 'admin' | 'buyer' | 'creative') => api.get('/auth/users-by-role', { params: { role } }),
};

// Vertical Heads endpoints
export const verticalHeadsApi = {
  getAll: () => api.get('/vertical-heads'),
};

// Editor endpoints
export const editorApi = {
  getAll: (includeStats = false) =>
    api.get(`/editors${includeStats ? '?includeStats=true' : ''}`),
  getOne: (id: string) => api.get(`/editors/${id}`),
  create: (data: { name: string; display_name?: string }) =>
    api.post('/editors', data),
  update: (id: string, data: Partial<{ name: string; display_name: string; is_active: boolean }>) =>
    api.patch(`/editors/${id}`, data),
  delete: (id: string) => api.delete(`/editors/${id}`),
};

// Media endpoints
export const mediaApi = {
  upload: (
    file: File,
    editorId: string,
    tags?: string[],
    description?: string,
    metadataOptions?: { removeMetadata?: boolean; addMetadata?: boolean },
    folderOptions?: { folderId?: string; organizeByDate?: boolean; assignedBuyerId?: string },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('editor_id', editorId);
    if (tags) formData.append('tags', JSON.stringify(tags));
    if (description) formData.append('description', description);

    // ✨ Metadata options
    if (metadataOptions?.removeMetadata) {
      formData.append('remove_metadata', 'true');
    }
    if (metadataOptions?.addMetadata) {
      formData.append('add_metadata', 'true');
    }

    // ✨ NEW: Folder options
    if (folderOptions?.folderId) {
      formData.append('folder_id', folderOptions.folderId);
    }
    if (folderOptions?.organizeByDate) {
      formData.append('organize_by_date', 'true');
    }
    if (folderOptions?.assignedBuyerId) {
      formData.append('assigned_buyer_id', folderOptions.assignedBuyerId);
    }

    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getAll: (params?: {
    editor_id?: string;
    media_type?: string;
    tags?: string;
    search?: string;
    limit?: number;
    offset?: number;
    uploaded_by?: string;
    folder_id?: string;
    [key: string]: any;
  }) => api.get('/media', { params }),
  getOne: (id: string) => api.get(`/media/${id}`),
  update: (id: string, data: { editor_id?: string; tags?: string[]; description?: string }) =>
    api.patch(`/media/${id}`, data),
  rename: (id: string, newFilename: string) =>
    api.patch(`/media/${id}/rename`, { new_filename: newFilename }),
  delete: (id: string) => api.delete(`/media/${id}`),
  getStats: () => api.get('/media/stats'),

  // ✨ NEW: Bulk metadata operations
  bulkMetadata: (data: {
    file_ids: string[];
    operation: 'add' | 'remove' | 'remove_and_add';
    metadata?: any;
  }) => api.post('/media/bulk/metadata', data),

  getBulkStatus: (jobId: string) => api.get(`/media/bulk/status/${jobId}`),

  cancelBulkOperation: (jobId: string) => api.post(`/media/bulk/cancel/${jobId}`),

  // Metadata extraction and viewing
  extractMetadata: (id: string) => api.get(`/media/${id}/metadata`),

  // Bulk ZIP download (optional folderMap: { fileId: 'folder/path' } for tree structure)
  bulkDownloadZip: (fileIds: string[], folderMap?: Record<string, string>) =>
    api.post('/media/bulk/download-zip', { file_ids: fileIds, folder_map: folderMap || undefined }, {
      responseType: 'blob',
      timeout: 600000, // 10 min timeout for large ZIPs
    }),

  // ✨ NEW: File versioning
  getVersionHistory: (id: string) => api.get(`/media/${id}/versions`),
  createVersion: (id: string, file: File, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }
    return api.post(`/media/${id}/versions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  restoreVersion: (id: string, versionId: string) =>
    api.post(`/media/${id}/versions/${versionId}/restore`),
  deleteVersion: (id: string, versionId: string) =>
    api.delete(`/media/${id}/versions/${versionId}`),

  // ✨ NEW: Bulk operations
  bulkDelete: (fileIds: string[]) =>
    api.delete('/media/bulk', { data: { file_ids: fileIds } }),
  bulkMove: (fileIds: string[], targetFolderId: string | null) =>
    api.post('/media/bulk/move', { file_ids: fileIds, target_folder_id: targetFolderId }),
  bulkCopy: (fileIds: string[], targetFolderId: string | null) =>
    api.post('/media/bulk/copy', { file_ids: fileIds, target_folder_id: targetFolderId }),

  // ✨ NEW: Individual file move/copy operations
  moveFile: (fileId: string, targetFolderId: string | null) =>
    api.post(`/media/${fileId}/move`, { target_folder_id: targetFolderId }),
  copyFile: (fileId: string, targetFolderId: string | null) =>
    api.post(`/media/${fileId}/copy`, { target_folder_id: targetFolderId }),

  // ✨ NEW: Trash / Deleted files operations
  getDeletedFiles: () => api.get('/media/deleted'),
  restore: (fileId: string) => api.post(`/media/${fileId}/restore`),
  permanentDelete: (fileId: string) => api.delete(`/media/${fileId}/permanent`),
  emptyTrash: () => api.delete('/media/deleted/empty'),

  // ✨ NEW: Add file request upload to media library
  addFileRequestUploadToLibrary: (fileId: string) =>
    api.post(`/media/add-from-file-request/${fileId}`),
};

// Facebook endpoints
export const facebookApi = {
  connect: (data: { accessToken: string; adAccountId?: string; adAccountName?: string }) =>
    api.post('/facebook/connect', data),
  getAdAccounts: () => api.get('/facebook/ad-accounts'),
  updateAdAccount: (data: { adAccountId: string; adAccountName: string }) =>
    api.put('/facebook/ad-account', data),
  getStatus: () => api.get('/facebook/status'),
  disconnect: () => api.delete('/facebook/disconnect'),
};

// Admin endpoints
export const adminApi = {
  getUsers: () => api.get('/admin/users'),
  createUser: (data: {
    name: string;
    email: string;
    password: string;
    role: string;
    upload_limit_monthly?: number;
  }) => api.post('/admin/users', data),
  updateUser: (id: string, data: Partial<{
    name: string;
    role: string;
    upload_limit_monthly: number;
    is_active: boolean;
    assigned_verticals: string[];
    additional_roles: string[];
  }>) => api.patch(`/admin/users/${id}`, data),
  getStats: () => api.get('/admin/stats'),

  // Vertical management
  getVerticals: () => api.get('/admin/verticals'),
  createVertical: (data: { name: string }) => api.post('/admin/verticals', data),
  updateVertical: (id: string, data: { name?: string; is_active?: boolean }) => api.patch(`/admin/verticals/${id}`, data),
  deleteVertical: (id: string) => api.delete(`/admin/verticals/${id}`),

  // Approval workflow
  getPendingUsers: () => api.get('/admin/pending-users'),
  approveUser: (id: string) => api.post(`/admin/approve-user/${id}`),
  rejectUser: (id: string, data: { reason?: string }) =>
    api.post(`/admin/reject-user/${id}`, data),

  // Password management
  resetUserPassword: (id: string, data: { admin_password: string; new_password: string }) =>
    api.post(`/admin/users/${id}/reset-password`, data),

  // Email whitelist
  getAllowedEmails: (params?: { limit?: number; offset?: number }) =>
    api.get('/admin/allowed-emails', { params }),
  addAllowedEmail: (data: { email: string; department?: string; job_title?: string; notes?: string }) =>
    api.post('/admin/allowed-emails', data),
  bulkImportEmails: (data: { emails: Array<{ email: string; department?: string; job_title?: string; notes?: string }> }) =>
    api.post('/admin/allowed-emails/bulk-import', data),
  removeAllowedEmail: (id: string) => api.delete(`/admin/allowed-emails/${id}`),
};

// RBAC endpoints
export const rbacApi = {
  getPermissionMatrix: () => api.get('/rbac/permissions'),
  getMyPermissions: () => api.get('/rbac/permissions/me'),
};

// Activity Logs endpoints (Admin only)
export const activityLogApi = {
  getLogs: (params?: {
    user_id?: string;
    user_email?: string;
    user_emails?: string; // comma-separated list for the multi-select email filter
    action_type?: string;
    resource_type?: string;
    status?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/activity-logs', { params }),
  getFilters: () => api.get('/activity-logs/filters'),
};

// Activity endpoints (per-file activity)
export const activityApi = {
  getFileActivity: (fileId: string, params?: {
    action_type?: string;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }) => api.get(`/media/${fileId}/activity`, { params }),
};

// Team endpoints - Phase 8 Teams Feature (comprehensive)
// Note: This replaces the previous simple teamApi with full Phase 8 functionality

// Permission endpoints
export const permissionApi = {
  grant: (data: {
    resource_type: 'file' | 'folder';
    resource_id: string;
    grantee_type: 'user' | 'team';
    grantee_id: string;
    permission_type: 'view' | 'download' | 'edit' | 'delete';
    expires_at?: string;
  }) => api.post('/permissions', data),
  getResourcePermissions: (resourceType: string, resourceId: string) =>
    api.get('/permissions', { params: { resource_type: resourceType, resource_id: resourceId } }),
  revoke: (id: string) => api.delete(`/permissions/${id}`),
  shareFolder: (data: {
    folder_id: string;
    team_id: string;
    permissions: string[];
  }) => api.post('/permissions/share-folder', data),
  getSharedByMe: () => api.get('/permissions/shared-by-me'),
  getSharedWithMe: () => api.get('/permissions/shared-with-me'),
};

// Public Link endpoints
export const publicLinkApi = {
  create: (permissionId: string, options: {
    password?: string;
    expires_at?: string;
    disable_download?: boolean;
    max_views?: number;
  }) => api.post(`/permissions/${permissionId}/public-link`, options),

  update: (linkId: string, options: {
    password?: string;
    expires_at?: string;
    disable_download?: boolean;
    max_views?: number;
  }) => api.patch(`/permissions/public-link/${linkId}`, options),

  revoke: (linkId: string) => api.delete(`/permissions/public-link/${linkId}`),

  getStats: (linkId: string) => api.get(`/permissions/public-link/${linkId}/stats`),

  // Public endpoints (no auth required)
  verifyPassword: (token: string, password?: string) =>
    axios.post(`${API_BASE_URL}/permissions/public/verify`, { token, password }),

  getPublic: (token: string) =>
    axios.get(`${API_BASE_URL}/permissions/public/${token}`),

  downloadPublic: (token: string, password?: string) =>
    axios.get(`${API_BASE_URL}/permissions/public/${token}/download`, {
      params: password ? { password } : undefined,
    }),
};

// ✨ NEW: Folder endpoints
export const folderApi = {
  // Create new folder
  create: (data: {
    name: string;
    parent_folder_id?: string;
    description?: string;
    color?: string;
    team_id?: string;
    ownership_type?: 'user' | 'team';
  }) => api.post('/folders', data),

  // Get folder tree (hierarchical structure)
  getTree: (params?: { parent_id?: string; include_deleted?: boolean }) =>
    api.get('/folders/tree', { params }),

  // Get single folder details
  getOne: (id: string) => api.get(`/folders/${id}`),

  // Get folder contents (subfolders + files)
  getContents: (id: string, params?: {
    page?: number;
    limit?: number;
    file_type?: string;
  }) => api.get(`/folders/${id}/contents`, { params }),

  // Get folder breadcrumb (navigation path)
  getBreadcrumb: (id: string) => api.get(`/folders/${id}/breadcrumb`),

  // Get sibling folders (folders at same level)
  getSiblings: (id: string) => api.get(`/folders/${id}/siblings`),

  // Update folder (rename, change description, color)
  update: (id: string, data: Partial<{
    name: string;
    description: string;
    color: string;
  }>) => api.patch(`/folders/${id}`, data),

  // Rename folder
  rename: (id: string, newName: string) =>
    api.patch(`/folders/${id}/rename`, { new_name: newName }),

  // Delete folder
  delete: (id: string, deleteContents = false) =>
    api.delete(`/folders/${id}?delete_contents=${deleteContents}`),

  // Move files to target folder
  moveFiles: (data: { file_ids: string[]; target_folder_id: string | null }) =>
    api.post('/folders/move-files', data),

  // Copy files to target folder
  copyFiles: (data: { file_ids: string[]; target_folder_id: string | null }) =>
    api.post('/folders/copy-files', data),

  // Create or get date-based folder structure (jan2024/15-jan/)
  createDateFolder: (data?: { date?: string; parent_folder_id?: string }) =>
    api.post('/folders/date-folder', data),

  // Download folder as ZIP
  downloadFolder: (id: string) =>
    api.get(`/folders/${id}/download`, {
      responseType: 'blob',
    }),

  // Toggle folder lock
  toggleLock: (id: string, reason?: string) =>
    api.post(`/folders/${id}/toggle-lock`, { reason }),

  // Get folder lock status
  getLockStatus: (id: string) =>
    api.get(`/folders/${id}/lock-status`),

  // Folder access management
  grantAccess: (folderId: string, data: {
    userId: string;
    permissionType: 'view' | 'edit' | 'delete';
    expiresAt?: string;
  }) => api.post(`/folders/${folderId}/grant-access`, data),

  getPermissions: (folderId: string) =>
    api.get(`/folders/${folderId}/permissions`),

  revokeAccess: (folderId: string, permissionId: string) =>
    api.delete(`/folders/${folderId}/permissions/${permissionId}`),

  searchUsers: (query: string) =>
    api.get(`/folders/search-users`, { params: { q: query } }),
};

// File Request Comments endpoints
export const requestCommentsApi = {
  getComments: (requestId: string) =>
    api.get(`/file-requests/${requestId}/comments`),

  addComment: (requestId: string, comment: string) =>
    api.post(`/file-requests/${requestId}/comments`, { comment }),

  updateComment: (commentId: string, comment: string) =>
    api.put(`/file-requests/comments/${commentId}`, { comment }),

  deleteComment: (commentId: string) =>
    api.delete(`/file-requests/comments/${commentId}`),

  getCommentCount: (requestId: string) =>
    api.get(`/file-requests/${requestId}/comments/count`),
};

// Starred endpoints
export const starredApi = {
  // Toggle starred status for a file
  toggleStarred: (fileId: string, isStarred: boolean) =>
    api.put(`/starred/${fileId}`, { is_starred: isStarred }),

  // Get all starred files
  getStarredFiles: () => api.get('/starred'),
};

// Comment endpoints
export const commentApi = {
  // Get comments for a file
  getComments: (fileId: string, includeResolved = true) =>
    api.get('/comments', { params: { file_id: fileId, include_resolved: includeResolved } }),

  // Create a new comment
  createComment: (data: {
    file_id: string;
    content: string;
    parent_comment_id?: string;
  }) => api.post('/comments', data),

  // Update comment
  updateComment: (id: string, content: string) =>
    api.patch(`/comments/${id}`, { content }),

  // Delete comment
  deleteComment: (id: string) => api.delete(`/comments/${id}`),

  // Toggle comment resolution
  toggleResolve: (id: string) => api.post(`/comments/${id}/resolve`),

  // Add reaction to comment
  addReaction: (id: string, reactionType: string) =>
    api.post(`/comments/${id}/reactions`, { reaction_type: reactionType }),

  // Remove reaction from comment
  removeReaction: (id: string, reactionType: string) =>
    api.delete(`/comments/${id}/reactions/${reactionType}`),
};

// Saved Search / Smart Collections endpoints
export const savedSearchApi = {
  create: (data: {
    name: string;
    description?: string;
    filters: any;
    color?: string;
    icon?: string
  }) => api.post('/saved-searches', data),

  getAll: () => api.get('/saved-searches'),

  getOne: (id: string) => api.get(`/saved-searches/${id}`),

  getResults: (id: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/saved-searches/${id}/results`, { params }),

  update: (id: string, data: any) => api.patch(`/saved-searches/${id}`, data),

  delete: (id: string) => api.delete(`/saved-searches/${id}`),

  toggleFavorite: (id: string) => api.post(`/saved-searches/${id}/favorite`),
};

// Metadata endpoints
export const metadataApi = {
  // Add metadata to file (embed creator info)
  addMetadata: (
    file: File,
    creatorId: string,
    options?: {
      description?: string;
      title?: string;
      keywords?: string;
      custom_fields?: Record<string, any>;
    }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('creator_id', creatorId);
    if (options?.description) formData.append('description', options.description);
    if (options?.title) formData.append('title', options.title);
    if (options?.keywords) formData.append('keywords', options.keywords);
    if (options?.custom_fields) formData.append('custom_fields', JSON.stringify(options.custom_fields));

    return api.post('/metadata/add', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Remove metadata from file (strip EXIF/GPS)
  removeMetadata: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/metadata/remove', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Extract metadata from file (read-only)
  extractMetadata: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post('/metadata/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Cost captured at close time for LEGACY requests whose uploaded files have no per-file cost yet.
// Per-file array is preferred; total_cost_usd is the fallback (backend splits it by rate-card weight).
export interface DeliveryCostPayload {
  file_costs?: Array<{
    media_file_id?: string;
    upload_id?: string;
    cost_usd: number;
    duration_seconds?: number;
  }>;
  total_cost_usd?: number;
  cost_note?: string;
}

// File Request endpoints
export const fileRequestApi = {
  // Create new file request
  create: (data: {
    title?: string;
    request_type?: string;
    description?: string;
    concept_notes?: string;
    num_creatives?: number;
    platform?: string;
    vertical?: string;
    platforms?: string[];  // 🆕 Multi-platform support
    verticals?: string[];  // 🆕 Multi-vertical support
    folder_id?: string;
    deadline?: string;
    allow_multiple_uploads?: boolean;
    require_email?: boolean;
    custom_message?: string;
    editor_id?: string;
    editor_ids?: string[];
    assigned_buyer_id?: string;
    assigned_buyer_ids?: string[];
    team_name?: string;
    is_winner_variation?: boolean;
    source_winner?: string;
    varied_axis?: string;
    orbit_context?: string;   // the work order ORBIT reads (scripts, source clips, state, offer)
    orbit_auto?: boolean;     // auto-fulfil this request with ORBIT on create
    duration_plan?: string;   // buyer's per-creative duration split (free text)
    cost_confirmed?: boolean; // buyer accepted the projected AI spend
  }) => api.post('/file-requests', data),

  // Preview the projected AI spend for a duration plan (calculation only, no writes)
  estimateCost: (duration_plan: string, num_creatives: number, request_type?: string) =>
    api.post('/file-requests/estimate-cost', { duration_plan, num_creatives, request_type }),

  // Get all file requests for current user (paginated)
  getAll: (params?: { status?: string; page?: number; limit?: number; search?: string; vertical?: string; platform?: string; [key: string]: any }) =>
    api.get('/file-requests', { params }),

  // Get single file request details
  getOne: (id: string) => api.get(`/file-requests/${id}`),

  // Pre-flight name validation (HI naming counter — HAM/RIT/NAN on HI requests)
  validateName: (id: string, filename: string) =>
    api.post(`/file-requests/${id}/validate-name`, { filename }),

  // Reference/reuse an existing media file into this request (no re-upload)
  attachExisting: (id: string, fileId: string, comments?: string) =>
    api.post(`/file-requests/${id}/attach-existing`, { fileId, comments }),

  // Update file request
  update: (id: string, data: {
    title?: string;
    description?: string;
    deadline?: string;
    allow_multiple_uploads?: boolean;
    require_email?: boolean;
    custom_message?: string;
    public_download_link?: string;
  }) => api.patch(`/file-requests/${id}`, data),

  // Status transition methods
  markAsUploaded: (id: string, data?: { actual_cost_usd?: number; cost_note?: string }) =>
    api.post(`/file-requests/${id}/mark-uploaded`, data || {}),
  markProgress: (id: string, data: { completedCount: number; note?: string; editorId?: string; actual_cost_usd?: number; cost_note?: string }) =>
    api.post(`/file-requests/${id}/mark-progress`, data),
  syncEditorStatuses: (id: string) => api.post(`/file-requests/${id}/sync-editor-statuses`),
  launch: (id: string, data?: DeliveryCostPayload) => api.post(`/file-requests/${id}/launch`, data || {}),
  closeRequest: (id: string, data?: DeliveryCostPayload) => api.post(`/file-requests/${id}/close`, data || {}),
  reopenRequest: (id: string) => api.post(`/file-requests/${id}/reopen`),

  // Backward compatibility (deprecated)
  close: (id: string, data?: DeliveryCostPayload) => api.post(`/file-requests/${id}/close`, data || {}),

  // Delete file request
  delete: (id: string) => api.delete(`/file-requests/${id}`),

  // Public endpoints (no auth required)
  getPublic: (token: string) =>
    axios.get(`${API_BASE_URL}/file-requests/public/${token}`),

  uploadToRequest: (token: string, file: File, uploaderEmail?: string, uploaderName?: string, editorId?: string, comments?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (uploaderEmail) formData.append('uploader_email', uploaderEmail);
    if (uploaderName) formData.append('uploader_name', uploaderName);
    if (editorId) formData.append('editor_id', editorId);
    if (comments) formData.append('comments', comments);
    return axios.post(`${API_BASE_URL}/file-requests/public/${token}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Assign multiple editors to file request
  assignEditors: (id: string, editor_ids: string[]) =>
    api.post(`/file-requests/${id}/assign-editors`, { editor_ids }),

  // Create folder for file request
  createFolder: (id: string, data: { folder_name: string; description?: string }) =>
    api.post(`/file-requests/${id}/folders`, data),

  // Get folders for file request
  getFolders: (id: string) => api.get(`/file-requests/${id}/folders`),

  // Move files to a sub-folder
  moveFilesToFolder: (requestId: string, folderId: string, uploadIds: string[]) =>
    api.patch(`/file-requests/${requestId}/folders/${folderId}/files`, { upload_ids: uploadIds }),

  // Move files back to unfiled
  unfileFiles: (requestId: string, uploadIds: string[]) =>
    api.patch(`/file-requests/${requestId}/folders/unfiled/files`, { upload_ids: uploadIds }),

  // Reorder files within a request (by media file IDs)
  reorderFiles: (requestId: string, orderedFileIds: string[]) =>
    api.patch(`/file-requests/${requestId}/reorder`, { ordered_file_ids: orderedFileIds }),

  // Rename a sub-folder
  updateFolder: (requestId: string, folderId: string, data: { folder_name: string; description?: string }) =>
    api.patch(`/file-requests/${requestId}/folders/${folderId}`, data),

  // Delete a sub-folder
  deleteFolder: (requestId: string, folderId: string) =>
    api.delete(`/file-requests/${requestId}/folders/${folderId}`),

  // Get assigned editors for file request
  getAssignedEditors: (id: string) => api.get(`/file-requests/${id}/editors`),

  // Complete file request
  complete: (id: string, data?: { delivery_note?: string }) =>
    api.post(`/file-requests/${id}/complete`, data),

  // Reassign file request with creative distribution and per-editor quotas (supports all legacy formats)
  reassign: (id: string, data: {
    reassign_to?: string;  // Legacy single editor assignment
    editor_ids?: string[];  // Multiple editor assignment
    editor_quotas?: Record<string, number>;  // Per-editor quotas (main branch feature)
    note?: string;         // Legacy reassignment note
    reason?: string;       // 🆕 New reassignment reason field
    editor_distribution?: Array<{ editor_id: string; num_creatives: number }>;  // 🆕 Creative distribution
    editor_notes?: Record<string, string>;  // 🆕 Per-editor reassignment notes
  }) => api.post(`/file-requests/${id}/reassign`, data),

  // Get reassignment history
  getReassignments: (id: string) => api.get(`/file-requests/${id}/reassignments`),

  // Reset all assignments (admin only)
  resetAssignments: (id: string) => api.post(`/file-requests/${id}/reset-assignments`),

  // Get upload history for file request
  getUploadHistory: (id: string) => api.get(`/file-requests/${id}/upload-history`),

  // Soft-remove an upload session (tracks deletions/removals)
  deleteUploadSession: (requestId: string, uploadId: string) =>
    api.delete(`/file-requests/${requestId}/uploads/${uploadId}`),

  // Authenticated upload to file request (for editors)
  uploadToRequestAuth: (id: string, file: File, comments?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (comments) formData.append('comments', comments);
    return api.post(`/file-requests/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Canvas/Product Brief endpoints
  canvas: {
    // Get canvas for file request
    get: (requestId: string) =>
      api.get(`/file-requests/${requestId}/canvas`),

    // Create or update canvas
    upsert: (requestId: string, content: any) =>
      api.post(`/file-requests/${requestId}/canvas`, { content }),

    // Upload attachment to canvas
    uploadAttachment: (requestId: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/file-requests/${requestId}/canvas/attach`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    // Remove attachment from canvas
    removeAttachment: (requestId: string, fileId: string) =>
      api.delete(`/file-requests/${requestId}/canvas/attachments/${fileId}`),

    // Copy attachments from source request canvas to target request canvas
    copyAttachments: (targetRequestId: string, sourceRequestId: string) =>
      api.post(`/file-requests/${targetRequestId}/canvas/copy-attachments`, { sourceRequestId }),

    // Delete canvas
    delete: (requestId: string) =>
      api.delete(`/file-requests/${requestId}/canvas`),
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Launch Request API
// ─────────────────────────────────────────────────────────────────────────────

export const launchRequestApi = {
  // Create new launch request
  create: (data: {
    request_type?: string;
    concept_notes?: string;
    num_creatives?: number;
    suggested_run_qty?: number;
    notes_to_creative?: string;
    notes_to_buyer?: string;
    platforms?: string[];
    verticals?: string[];
    delivery_deadline?: string;
    test_deadline?: string;
    folder_id?: string;
    creative_head_id?: string;
    buyer_head_id?: string;
    editor_ids?: string[];
    editor_distribution?: Array<{ editor_id: string; num_creatives: number }>;
    buyer_ids?: string[];
    save_as_template?: boolean;
    template_name?: string;
    status?: 'draft' | 'pending_review';
  }) => api.post('/launch-requests', data),

  // Get all (role-filtered)
  getAll: (params?: {
    status?: string;
    vertical?: string;
    platform?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }) => api.get('/launch-requests', { params }),

  // Get single
  getOne: (id: string) => api.get(`/launch-requests/${id}`),

  // Update fields
  update: (id: string, data: {
    concept_notes?: string;
    delivery_deadline?: string;
    test_deadline?: string;
    committed_run_qty?: number;
    committed_test_deadline?: string;
    notes_to_creative?: string;
    notes_to_buyer?: string;
  }) => api.patch(`/launch-requests/${id}`, data),

  // Delete
  delete: (id: string) => api.delete(`/launch-requests/${id}`),

  // ── Status transitions ──────────────────────────────────────────────────
  submit: (id: string) => api.post(`/launch-requests/${id}/submit`),
  acceptByCreativeHead: (id: string) => api.post(`/launch-requests/${id}/accept`),
  markReadyToLaunch: (id: string) => api.post(`/launch-requests/${id}/mark-ready`),
  assignBuyers: (id: string, data: {
    buyer_assignments: Array<{ buyer_id: string; file_ids?: string[]; run_qty?: number; test_deadline?: string }>;
    committed_run_qty?: number;
    committed_test_deadline?: string;
  }) => api.post(`/launch-requests/${id}/assign-buyers`, data),
  launch: (id: string) => api.post(`/launch-requests/${id}/launch`),
  close: (id: string) => api.post(`/launch-requests/${id}/close`),
  reopen: (id: string) => api.post(`/launch-requests/${id}/reopen`),

  // ── Reassignment ────────────────────────────────────────────────────────
  reassignCreativeHead: (id: string, data: { new_creative_head_id: string; reason?: string }) =>
    api.post(`/launch-requests/${id}/reassign-creative-head`, data),
  reassignBuyerHead: (id: string, data: { new_buyer_head_id: string; reason?: string }) =>
    api.post(`/launch-requests/${id}/reassign-buyer-head`, data),

  // ── Editor assignment ───────────────────────────────────────────────────
  assignEditors: (id: string, data: {
    editor_distribution?: Array<{ editor_id: string; num_creatives: number }>;
    editor_ids?: string[];
    editor_notes?: Record<string, string>;
    reason?: string;
  }) => api.post(`/launch-requests/${id}/assign-editors`, data),

  // ── Buyer access management ─────────────────────────────────────────────
  /** Revoke a buyer's access — soft-deletes their media library folder + files */
  revokeAccess: (id: string, buyerId: string) =>
    api.delete(`/launch-requests/${id}/buyers/${buyerId}/access`),

  // ── Upload ──────────────────────────────────────────────────────────────
  upload: (id: string, file: File, comments?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (comments) formData.append('comments', comments);
    // Do NOT set Content-Type manually — browser must set multipart/form-data WITH boundary.
    // The axios instance default (application/json) must be cleared too.
    return api.post(`/launch-requests/${id}/upload`, formData, {
      headers: { 'Content-Type': undefined },
    });
  },

  // ── Download uploaded file ──────────────────────────────────────────────
  downloadUpload: (launchRequestId: string, uploadId: string) =>
    api.get(`/launch-requests/${launchRequestId}/uploads/${uploadId}/download`),

  // ── Templates ───────────────────────────────────────────────────────────
  getTemplates: () => api.get('/launch-requests/templates/list'),
  saveTemplate: (data: {
    name: string;
    default_request_type?: string;
    default_platforms?: string[];
    default_verticals?: string[];
    default_num_creatives?: number;
    default_suggested_run_qty?: number;
    default_concept_notes?: string;
    default_notes_to_creative?: string;
    default_notes_to_buyer?: string;
  }) => api.post('/launch-requests/templates', data),
  deleteTemplate: (templateId: string) => api.delete(`/launch-requests/templates/${templateId}`),

  // ── Canvas Brief ────────────────────────────────────────────────────────
  canvas: {
    get: (launchRequestId: string) => api.get(`/launch-requests/${launchRequestId}/canvas`),
    upsert: (launchRequestId: string, content: any, attachments?: any[]) =>
      api.post(`/launch-requests/${launchRequestId}/canvas`, { content, attachments }),
    uploadAttachment: (launchRequestId: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/launch-requests/${launchRequestId}/canvas/attach`, formData, {
        headers: { 'Content-Type': undefined },
      });
    },
    removeAttachment: (launchRequestId: string, fileId: string) =>
      api.delete(`/launch-requests/${launchRequestId}/canvas/attachments/${fileId}`),
  },
};

// Notification endpoints
export const notificationApi = {
  getAll: (params?: { limit?: number; offset?: number; unread_only?: boolean }) =>
    api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  getPendingCounts: () => api.get('/notifications/pending-counts'),
  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`, {}),
  markAllAsRead: () => api.post('/notifications/mark-all-read', {}),
  delete: (id: string) => api.delete(`/notifications/${id}`),
};

// Slack Integration endpoints
export const slackApi = {
  // Initiate OAuth flow
  initiateOAuth: () => api.get('/slack/oauth/initiate'),

  // Get connection status
  getStatus: () => api.get('/slack/status'),

  // Disconnect Slack
  disconnect: () => api.post('/slack/disconnect'),

  // Get notification preferences
  getPreferences: () => api.get('/slack/preferences'),

  // Update notification preferences
  updatePreferences: (data: { enabled?: boolean; preferences?: Record<string, boolean> }) =>
    api.put('/slack/preferences', data),

  // Get all Slack-connected users
  getConnectedUsers: () => api.get('/slack/connected-users'),

  // Send manual notification to selected users
  sendManualNotification: (data: {
    userIds: string[];
    fileName: string;
    fileUrl?: string;
    message?: string;
  }) => api.post('/slack/notify', data),

  // Send test notification
  sendTest: () => api.post('/slack/test'),
};

// Activity Log Export endpoints (admin only)
export const activityLogExportApi = {
  // Get export history
  getHistory: () => api.get('/activity-logs/exports'),

  // Get specific export
  getExport: (exportId: string) => api.get(`/activity-logs/exports/${exportId}`),

  // Get download URL for export
  getDownloadUrl: (exportId: string) => api.get(`/activity-logs/exports/${exportId}/download`),

  // Trigger manual export
  manualExport: (targetDate: string) =>
    api.post('/activity-logs/exports/manual', { targetDate }),

  // Get export job status
  getJobStatus: () => api.get('/activity-logs/exports/job/status'),

  // Request new export
  requestExport: (data: { start_date: string; end_date: string }) =>
    api.post('/activity-logs/exports/request', data),
};

// Enhanced Analytics endpoints
export const teamLeadDashboardApi = {
  getAccess: () => api.get('/team-lead-dashboard/access'),
  // Verticals the current user may open in the dashboard dropdown.
  getViewableVerticals: () => api.get('/team-lead-dashboard/verticals'),
  getWinners: (params?: { date_from?: string; date_to?: string; vertical?: string }) =>
    api.get('/team-lead-dashboard/home-insurance/winners', { params }),
  getHomeInsurancePerformance: (params?: { date_from?: string; date_to?: string; level?: 'campaign' | 'ad'; vertical?: string }) =>
    api.get('/team-lead-dashboard/home-insurance', { params }),
  getDuplicates: (params?: { date_from?: string; date_to?: string; vertical?: string }) =>
    api.get('/team-lead-dashboard/home-insurance/duplicates', { params }),
  listDecisions: (params?: { vertical?: string }) =>
    api.get('/team-lead-dashboard/home-insurance/decisions', { params }),
  setDecision: (data: { creative_key: string; status: 'declined' | 'done' | 'pending'; note?: string; vertical?: string }) => {
    const { vertical, ...body } = data;
    // The vertical is resolved server-side from the query string (not the body).
    return api.post('/team-lead-dashboard/home-insurance/decisions', body, { params: vertical ? { vertical } : undefined });
  },
  triggerSlackDigestTest: () =>
    api.post('/team-lead-dashboard/home-insurance/slack-test'),
};

export const analyticsApi = {
  // Sync Facebook ads
  syncAds: (data: { ad_account_id: string; date_from?: string; date_to?: string }) =>
    api.post('/analytics/sync', data),

  // Stop ongoing sync
  stopSync: () => api.post('/analytics/sync/stop'),

  // Get editor performance with filtering
  getEditorPerformance: (params?: {
    editor_id?: string;
    date_from?: string;
    date_to?: string;
    media_type?: 'image' | 'video';
  }) => api.get('/analytics/editor-performance', { params }),

  // Get editor media uploads with filtering
  getEditorMedia: (params?: {
    editor_id?: string;
    date_from?: string;
    date_to?: string;
    media_type?: 'image' | 'video';
  }) => api.get('/analytics/editor-media', { params }),

  // Get ads without editor
  getAdsWithoutEditor: () => api.get('/analytics/ads-without-editor'),

  // Get ad name changes
  getAdNameChanges: (params?: { editor_changed?: boolean; date_from?: string }) =>
    api.get('/analytics/ad-name-changes', { params }),

  // Get unified analytics
  getUnifiedAnalytics: (params: {
    ad_account_id: string;
    date_from?: string;
    date_to?: string;
    bulk_fetch?: boolean;
  }) => api.get('/analytics/unified', { params }),

  // Get personal editor dashboard
  getEditorDashboard: () => api.get('/analytics/editor-dashboard'),

  // Get vertical dashboard analytics
  getVerticalDashboard: () => api.get('/analytics/vertical-dashboard'),

  // Get detailed granular requests for a specific vertical
  getVerticalDetailedRequests: (vertical: string) =>
    api.get(`/analytics/vertical-dashboard/${encodeURIComponent(vertical)}/details`),

  getCreativeUcodes: (params?: any) => api.get('/analytics/editor-media', { params }),
  getCreativeUcodeDetail: (ucode: string) => api.get(`/analytics/editor-media`, { params: { ucode } }),
  getCreativePerformance: (params?: any) => api.get('/analytics/editor-performance', { params }),
  syncCreativeMappings: (data?: any) => api.post('/ad-creative-mapping/run', data),
  importMetadataExcel: (formData: FormData, config?: any) => api.post('/analytics/import-metadata', formData, config),
  syncRedTrackData: (data?: any) => api.post('/analytics/sync-redtrack', data),

  // Analytics Engine unified endpoint
  getEngineData: (params?: {
    dateFrom?: string;
    dateTo?: string;
    editorId?: string;
    buyerId?: string;
    verticalId?: string;
    matchMethod?: string;
    level?: string;
  }) => api.get('/analytics/engine-data', { params }),

  getAnalyticsPipelineStatus: () => api.get('/analytics/pipeline-status'),

  getEditorCreatives: (params: { editorId: string; dateFrom?: string; dateTo?: string }) =>
    api.get('/analytics/editor-creatives', { params }),
};

// Metadata Tags endpoints
export const metadataTagApi = {
  // Get all tags with usage counts
  getAll: (params?: { category?: string; search?: string }) =>
    api.get('/metadata-tags', { params }),

  // Get tag by ID
  getById: (id: string) => api.get(`/metadata-tags/${id}`),

  // Create new tag
  create: (data: { name: string; category?: string; description?: string }) =>
    api.post('/metadata-tags', data),

  // Update tag
  update: (id: string, data: { name?: string; category?: string; description?: string }) =>
    api.patch(`/metadata-tags/${id}`, data),

  // Delete tag
  delete: (id: string) => api.delete(`/metadata-tags/${id}`),

  // Get all unique categories
  getCategories: () => api.get('/metadata-tags/categories'),

  // Get files with a specific tag
  getFilesWithTag: (id: string) => api.get(`/metadata-tags/${id}/files`),

  // Add tag to media file
  addTagToFile: (mediaId: string, tagId: string) =>
    api.post(`/media/${mediaId}/tags`, { tag_id: tagId }),

  // Remove tag from media file
  removeTagFromFile: (mediaId: string, tagId: string) =>
    api.delete(`/media/${mediaId}/tags/${tagId}`),

  // Get tags for a specific media file
  getFileTags: (mediaId: string) => api.get(`/media/${mediaId}/tags`),

  // Bulk add tags to file
  bulkAddTagsToFile: (mediaId: string, tagIds: string[]) =>
    api.post(`/media/${mediaId}/tags/bulk`, { tag_ids: tagIds }),
};

// Workload Management endpoints
export const workloadApi = {
  // Get workload overview for all editors (optional date range filter)
  getOverview: (params?: { from?: string; to?: string }) =>
    api.get('/workload/overview', { params }),

  // Get detailed workload for a specific editor (optional date range filter)
  getEditorWorkload: (editorId: string, params?: { from?: string; to?: string }) =>
    api.get(`/workload/editor/${editorId}`, { params }),

  // Update editor capacity settings
  updateCapacity: (editorId: string, data: {
    maxConcurrentRequests?: number;
    maxHoursPerWeek?: number;
    isAvailable?: boolean;
    unavailableUntil?: string;
    unavailableReason?: string;
  }) => api.put(`/workload/capacity/${editorId}`, data),

  // Update file request time estimate
  updateEstimate: (requestId: string, data: {
    estimatedHours?: number;
    complexity?: string;
    priority?: number;
  }) => api.put(`/workload/request/${requestId}/estimate`, data),

  // Get workload analytics
  getAnalytics: (params?: { startDate?: string; endDate?: string }) =>
    api.get('/workload/analytics', { params }),

  // Get workload recommendations
  getRecommendations: (requestId?: string) =>
    api.get('/workload/recommendations', { params: requestId ? { requestId } : {} }),
};

// Team endpoints
export const teamApi = {
  // Team management
  createTeam: (data: { name: string; description?: string }) =>
    api.post('/teams', data),
  create: (data: { name: string; description?: string }) =>
    api.post('/teams', data),
  getUserTeams: () =>
    api.get('/teams'),
  getAll: () =>
    api.get('/teams'),
  getTeam: (teamId: string) =>
    api.get(`/teams/${teamId}`),
  getById: (teamId: string) =>
    api.get(`/teams/${teamId}`),
  updateTeam: (teamId: string, data: { name?: string; description?: string }) =>
    api.put(`/teams/${teamId}`, data),
  deleteTeam: (teamId: string) =>
    api.delete(`/teams/${teamId}`),
  delete: (teamId: string) =>
    api.delete(`/teams/${teamId}`),

  // Team members
  getAvailableUsers: (teamId: string, params?: { search?: string }) =>
    api.get(`/teams/${teamId}/available-users`, { params }),
  addMember: (teamId: string, data: { userId: string; teamRole: 'lead' | 'member' | 'guest' }) =>
    api.post(`/teams/${teamId}/members`, data),
  removeMember: (teamId: string, userId: string) =>
    api.delete(`/teams/${teamId}/members/${userId}`),
  updateMemberRole: (teamId: string, userId: string, data: { teamRole: 'lead' | 'member' | 'guest' }) =>
    api.put(`/teams/${teamId}/members/${userId}/role`, data),

  // Team folders
  getTeamFolders: (teamId: string) =>
    api.get(`/teams/${teamId}/folders`),

  // Team activity
  getActivity: (teamId: string, params?: { type?: string; userId?: string; limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/activity`, { params }),
  logActivity: (teamId: string, data: { activityType: string; resourceType?: string; resourceId?: string; metadata?: any }) =>
    api.post(`/teams/${teamId}/activity`, data),

  // Request templates
  createTemplate: (teamId: string, data: {
    name: string;
    description?: string;
    defaultTitle?: string;
    defaultRequestType?: string;
    defaultInstructions?: string;
    defaultPriority?: 'low' | 'normal' | 'high' | 'urgent';
    defaultDueDays?: number;
    defaultPlatform?: string;
    defaultVertical?: string;
    defaultNumCreatives?: number;
    defaultAllowMultipleUploads?: boolean;
    defaultRequireEmail?: boolean;
    defaultCustomMessage?: string;
    requiredFields?: any[];
  }) =>
    api.post(`/teams/${teamId}/templates`, data),
  getTemplates: (teamId: string, params?: { active?: boolean }) =>
    api.get(`/teams/${teamId}/templates`, { params }),

  // Personal + accessible templates
  getAccessibleTemplates: () => api.get('/templates'),
  createPersonalTemplate: (data: any) => api.post('/templates', data),
  getTemplate: (templateId: string) =>
    api.get(`/teams/templates/${templateId}`),
  updateTemplate: (templateId: string, data: any) =>
    api.put(`/teams/templates/${templateId}`, data),
  deleteTemplate: (templateId: string) =>
    api.delete(`/teams/templates/${templateId}`),
  useTemplate: (templateId: string, overrides?: any) =>
    api.post(`/teams/templates/${templateId}/use`, overrides),

  // Team analytics
  getAnalyticsSummary: (teamId: string) =>
    api.get(`/teams/${teamId}/analytics/summary`),
  getAnalyticsTrends: (teamId: string, params?: { startDate?: string; endDate?: string; groupBy?: 'day' | 'week' | 'month' }) =>
    api.get(`/teams/${teamId}/analytics/trends`, { params }),
  getMemberAnalytics: (teamId: string) =>
    api.get(`/teams/${teamId}/analytics/members`),
  getRequestAnalytics: (teamId: string) =>
    api.get(`/teams/${teamId}/analytics/requests`),

  // Team messages/discussion
  getMessages: (teamId: string, params?: { limit?: number; offset?: number; parent_id?: string }) =>
    api.get(`/teams/${teamId}/messages`, { params }),
  postMessage: (teamId: string, data: { message_text: string; parent_message_id?: string; mentions?: string[]; attachments?: any[] }) =>
    api.post(`/teams/${teamId}/messages`, data),
  editMessage: (teamId: string, messageId: string, data: { message_text: string }) =>
    api.put(`/teams/${teamId}/messages/${messageId}`, data),
  deleteMessage: (teamId: string, messageId: string) =>
    api.delete(`/teams/${teamId}/messages/${messageId}`),
  markMessageAsRead: (teamId: string, messageId: string) =>
    api.post(`/teams/${teamId}/messages/${messageId}/read`),
  getUnreadCount: (teamId: string) =>
    api.get(`/teams/${teamId}/messages/unread-count`),

  // Smart Collections
  createCollection: (data: { name: string; description?: string; teamId?: string; collectionType?: 'manual' | 'smart'; smartRules?: any[]; isPublic?: boolean }) =>
    api.post('/teams/collections', data),
  getCollections: (params?: { teamId?: string; collectionType?: string }) =>
    api.get('/teams/collections', { params }),
  getCollection: (collectionId: string) =>
    api.get(`/teams/collections/${collectionId}`),
  updateCollection: (collectionId: string, data: any) =>
    api.put(`/teams/collections/${collectionId}`, data),
  deleteCollection: (collectionId: string) =>
    api.delete(`/teams/collections/${collectionId}`),
  addItemToCollection: (collectionId: string, data: { fileRequestUploadId: string }) =>
    api.post(`/teams/collections/${collectionId}/items`, data),
  removeItemFromCollection: (collectionId: string, itemId: string) =>
    api.delete(`/teams/collections/${collectionId}/items/${itemId}`),

  // Team Shared Media
  shareMediaWithTeam: (data: { teamId: string; fileRequestUploadId: string; shareMessage?: string }) =>
    api.post(`/media/share`, data),
  shareMediaWithMultipleTeams: (data: { teamIds: string[]; fileRequestUploadId: string; shareMessage?: string }) =>
    api.post(`/media/share-multiple`, data),
  getTeamSharedMedia: (teamId: string, params?: { limit?: number; offset?: number }) =>
    api.get(`/teams/${teamId}/shared-media`, { params }),
  removeSharedMedia: (teamId: string, fileId: string) =>
    api.delete(`/teams/${teamId}/shared-media/${fileId}`),
};

// Script Tracker API
export const scriptTrackerApi = {
  getAll: (params?: { status?: string; vertical?: string; editor_name?: string; date_from?: string; date_to?: string; sort_by?: string; sort_order?: string }) =>
    api.get('/script-tracker', { params }),
  getOne: (id: string) => api.get(`/script-tracker/${id}`),
  create: (data: { script_title: string; date?: string; vertical?: string; notes?: string; status?: string; paste_links?: string; editor_name?: string }) =>
    api.post('/script-tracker', data),
  update: (id: string, data: Partial<{ script_title: string; date: string; vertical: string; notes: string; status: string; paste_links: string; editor_name: string }>) =>
    api.put(`/script-tracker/${id}`, data),
  delete: (id: string) => api.delete(`/script-tracker/${id}`),
  getVerticals: () => api.get('/script-tracker/verticals'),
  getEditors: () => api.get('/script-tracker/editors'),
};

// Script Database API
export const scriptDatabaseApi = {
  getAll: (params?: { vertical?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/script-database', { params }),
  getOne: (id: string) => api.get(`/script-database/${id}`),
  getVariations: (id: string) => api.get(`/script-database/${id}/variations`),
  create: (data: { script_title: string; vertical: string; content?: string }) =>
    api.post('/script-database', data),
  createVariation: (id: string, data: { script_title?: string; content?: string }) =>
    api.post(`/script-database/${id}/variations`, data),
  getStates: (id: string) => api.get(`/script-database/${id}/states`),
  createState: (id: string, data: { state_code: string; script_title?: string; content?: string }) =>
    api.post(`/script-database/${id}/states`, data),
  update: (id: string, data: { script_title?: string; content?: string }) =>
    api.patch(`/script-database/${id}`, data),
  delete: (id: string) => api.delete(`/script-database/${id}`),
  getVerticalCodes: () => api.get('/script-database/vertical-codes'),
  listUnusedHi: (params?: { include_not_approved?: boolean }) =>
    api.get('/script-database/hi/unused', { params }),
  scanUsage: () => api.post('/script-database/scan-usage'),
  // Per-script-number usage summary for the HI script list (used/unused badge)
  getHiUsageSummary: () => api.get('/script-database/hi/usage-summary'),
  // Drill-down: where was S<n> used? Returns media files + campaigns.
  getScriptUsage: (scriptNumber: string) => api.get(`/script-database/hi/usage/${scriptNumber}`),
};

// Tracking Report API
export const trackingReportApi = {
  getMyEditor: () => api.get('/tracking-report/my-editor'),
  getLogs: (params?: {
    editor_id?: string;
    editor_ids?: string;
    status?: string;
    from?: string;
    to?: string;
    request_type?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/tracking-report/logs', { params }),
  getLog: (id: string) => api.get(`/tracking-report/logs/${id}`),
  createManualEntry: (data: { title: string; note?: string; editor_id?: string; date?: string }) =>
    api.post('/tracking-report/logs', data),
  updateLog: (id: string, data: { title?: string; status?: string }) =>
    api.patch(`/tracking-report/logs/${id}`, data),
  addNote: (id: string, data: { note: string }) =>
    api.post(`/tracking-report/logs/${id}/notes`, data),
  getCalendar: (params?: { editor_id?: string; editor_ids?: string; from?: string; to?: string; status?: string }) =>
    api.get('/tracking-report/calendar', { params }),
  getCalendarBars: (params?: { editor_id?: string; editor_ids?: string; from?: string; to?: string; status?: string }) =>
    api.get('/tracking-report/calendar-bars', { params }),
  getSummary: (params?: { editor_id?: string; editor_ids?: string; from?: string; to?: string; status?: string }) =>
    api.get('/tracking-report/summary', { params }),
  getDailyMatrix: (params?: { date_from?: string; date_to?: string; editor_ids?: string }) =>
    api.get('/tracking-report/daily-matrix', { params }),
  // Per-file drill-down behind a daily-matrix uploads cell (request, buyer, cost, link, test status).
  getEditorDayFiles: (params: { editor_id: string; day: string; file_type?: 'video' | 'image' }) =>
    api.get('/tracking-report/editor-day-files', { params }),
  exportExcel: (params?: { editor_ids?: string; from?: string; to?: string; status?: string }) =>
    api.get('/tracking-report/export', { params, responseType: 'blob' }),
};

// Campaign Intelligence
export const campaignIntelligenceApi = {
  getRedTrackData: (params: { date_from: string; date_to: string }) =>
    api.get('/campaign-intelligence/redtrack-data', { params }),
  getRedTrackStatus: () =>
    api.get('/campaign-intelligence/redtrack-status'),
};

// Quality Check
export const qualityCheckApi = {
  getQueue: (params?: { phase?: '1' | '2'; status?: string; limit?: number; offset?: number }) =>
    api.get('/quality-check/queue', { params }),
  getStats: () => api.get('/quality-check/stats'),
  getReview: (id: string) => api.get(`/quality-check/${id}`),
  submitReview: (id: string, data: { status: 'passed' | 'failed'; review_notes?: string }) =>
    api.patch(`/quality-check/${id}/review`, data),
  phase1Review: (id: string, data: { status: 'passed' | 'failed'; review_notes?: string }) =>
    api.patch(`/quality-check/${id}/phase1-review`, data),
  phase2Review: (id: string, data: { status: 'passed' | 'failed'; review_notes?: string }) =>
    api.patch(`/quality-check/${id}/phase2-review`, data),
  updateNotes: (id: string, review_notes: string) =>
    api.patch(`/quality-check/${id}/notes`, { review_notes }),
  revertReview: (id: string) =>
    api.patch(`/quality-check/${id}/revert`),
  skipReview: (id: string, reason?: string) =>
    api.patch(`/quality-check/${id}/skip`, { reason }),
};

// Bulk download API (pre-flight + job control).
// The actual download flow lives in DownloadProgressContext, which goes
// directly through fetch + ReadableStream so the progress widget can show
// real bytes received. These helpers are for callers that just need
// pre-flight info or to interrogate / cancel a job out-of-band.
export const bulkDownloadApi = {
  estimate: (fileIds: string[]) =>
    api.post('/media/bulk/estimate', { file_ids: fileIds }) as Promise<{
      data: {
        success: boolean;
        data: {
          file_count: number;
          total_bytes: number;
          estimated_seconds: number;
          exceeds_limit: boolean;
          limit_reason: string | null;
        }
      }
    }>,
  getStatus: (jobId: string) => api.get(`/media/bulk/zip-status/${jobId}`),
  cancel: (jobId: string) => api.post(`/media/bulk/zip-cancel/${jobId}`),
};

// Download Codes (per-buyer per-file 8-char codes)
export const downloadCodeApi = {
  // Generate (or fetch existing) code for buyer download
  generate: (mediaFileId: string, code_type: 'normal' | 'flexible') =>
    api.post(`/media/${mediaFileId}/download-with-code`, { code_type }),
  // Lookup info for a code (used by Campaign Lookup page + Chrome extension)
  lookup: (code: string) =>
    api.get(`/download-codes/lookup/${encodeURIComponent(code)}`),
};

// Chrome Extension pairing + key management
export const extensionApi = {
  generatePairingCode: () => api.post('/extension/pair'),
  exchangePairingCode: (pairing_code: string, device_label?: string) =>
    api.post('/extension/exchange', { pairing_code, device_label }),
  listKeys: () => api.get('/extension/keys'),
  revokeKey: (keyRef: string) => api.delete(`/extension/keys/${keyRef}`),
};

// Ad Creative Mapping API
export const adCreativeMappingApi = {
  getConfig: () => api.get('/ad-creative-mapping/config'),
  saveConfig: (data: Record<string, any>) => api.put('/ad-creative-mapping/config', data),
  saveConfigBulk: (configs: Array<{ key: string; value: any }>) =>
    api.put('/ad-creative-mapping/config/bulk', { configs }),
  runPipeline: (dateFrom?: string, dateTo?: string) =>
    api.post('/ad-creative-mapping/run', { dateFrom, dateTo }),
  getPipelineStatus: () => api.get('/ad-creative-mapping/pipeline-status'),
  cancelPipeline: () => api.post('/ad-creative-mapping/cancel', {}),
};

// Creative Usage report (delivered creatives launched vs not launched, verified against ads dashboard)
export const creativeUsageApi = {
  getUsage: (from?: string, to?: string) =>
    api.get('/ad-creative-mapping/creative-usage', { params: { from, to } }),
  getBuyerDetail: (buyer: string, limit = 100, offset = 0, from?: string, to?: string) =>
    api.get('/ad-creative-mapping/creative-usage', { params: { detail: 1, buyer, limit, offset, from, to } }),
  exportXlsx: (from?: string, to?: string) =>
    api.get('/ad-creative-mapping/creative-usage/export', { params: { from, to }, responseType: 'blob' }),
  // Delivered-but-never-launched creatives (with editor + buyer segregation)
  getUnmapped: (from?: string, to?: string, limit = 100, offset = 0) =>
    api.get('/ad-creative-mapping/creative-usage/unmapped', { params: { from, to, limit, offset } }),
  exportUnmapped: (from?: string, to?: string) =>
    api.get('/ad-creative-mapping/creative-usage/unmapped/export', { params: { from, to }, responseType: 'blob' }),
  // Buyer coverage note (which request-buyers appear in the report window)
  getCoverage: () => api.get('/ad-creative-mapping/coverage'),
  refresh: () => api.post('/ad-creative-mapping/refresh-launch-status'),
  getRunStatus: () => api.get('/ad-creative-mapping/launch-run-status'),
  // Per-vertical ad performance (real spend / revenue / profit / ROI across all platforms)
  getVerticalAdPerformance: (from?: string, to?: string) =>
    api.get('/ad-creative-mapping/vertical-ad-performance', { params: { from, to } }),
};

// Variation Studio (admin-only creative regeneration)
const _jobsCache: { at: number; promise: any } = { at: 0, promise: null };

// at most 3 thumbnail renders in flight; the rest wait their turn instead of timing out
let _thumbActive = 0;
const _thumbWaiting: Array<() => void> = [];
function _thumbQueue<T>(run: () => Promise<T>): Promise<T> {
  const start = () => {
    _thumbActive++;
    return run().finally(() => {
      _thumbActive--;
      const next = _thumbWaiting.shift();
      if (next) next();
    });
  };
  if (_thumbActive < 3) return start();
  return new Promise<T>((resolve, reject) => {
    _thumbWaiting.push(() => start().then(resolve, reject));
  });
}

export const regenApi = {
  getLosers: (params?: { minSpend?: number; maxRoas?: number; limit?: number }) =>
    api.get('/regen/losers', { params }),
  getCreative: (id: string) => api.get(`/regen/creative/${id}`),
  refreshTruth: () => api.post('/regen/refresh-truth'),
  interpret: (data: { creative_id: string; expectation: string; variation_type?: string | null }) =>
    api.post('/regen/interpret', data),
  createRequest: (data: {
    creative_id: string; variation_type?: string; expectation?: string;
    directive?: any; preserve?: string[]; variant_count?: number; model?: string;
    reference_creative_ids?: string[];
  }) => api.post('/regen/requests', data),
  listRequests: () => api.get('/regen/requests'),
  redispatch: (id: string) => api.post(`/regen/requests/${id}/redispatch`, {}),
  getRequest: (id: string) => api.get(`/regen/requests/${id}`),
  // Creative Lab — text→image (1-4 variants). Returns { ids }; poll each via getRequest.
  // mode 'ad' + template/headline/cta/subject/bullets → the compositor builds our real DR ad creatives.
  generateImages: (body: { prompt: string; image_model?: string; aspect?: string; count?: number; reference_image_url?: string;
    mode?: 'free' | 'ad'; template?: string; vertical?: string; headline?: string; cta?: string; subject?: string; bullets?: string[]; dry_run?: boolean }) =>
    api.post('/regen/image', body),
  approve: (id: string, variant_id: string) => api.post(`/regen/requests/${id}/approve`, { variant_id }),
  cancel: (id: string) => api.post(`/regen/requests/${id}/cancel`, {}),
  confirmCost: (id: string) => api.post(`/regen/requests/${id}/confirm-cost`, {}),
  adjustScript: (id: string, instruction: string) => api.post(`/regen/requests/${id}/adjust-script`, { instruction }),
  discard: (id: string, variant_id?: string) => api.post(`/regen/requests/${id}/discard`, variant_id ? { variant_id } : {}),
  abPackage: (id: string, variant_id: string) => api.get(`/regen/requests/${id}/ab-package`, { params: { variant_id } }),
  outcomes: () => api.get('/regen/outcomes'),
  scraperWinners: (vertical: string, limit = 12) => api.get('/regen/library/scraper-winners', { params: { vertical, limit } }),
  ourWinners: (params?: { minSpend?: number; minRoas?: number; limit?: number }) => api.get('/regen/library/our-winners', { params }),
  refine: (id: string, body: { feedback: string; variation_type?: string; model?: string }) => api.post(`/regen/requests/${id}/refine`, body),
  // Record a correction into the learning loop WITHOUT regenerating (no credit spent).
  // Feedback WITHOUT regenerating. Two-step: propose (interpret, write nothing) → confirm.
  // `confirmed:true` also writes the persona lesson, so this door ends in the same pipeline
  // as the Team Room. Pass creative_ref/variant_id to bind feedback to ONE clip rather than
  // condemning every sibling variant in the request.
  // Re-render ONE clip in context (anchored to the previous clip's final frame) instead of
  // dropping it or re-rolling the whole video. dry_run previews prompt + cost, spends nothing.
  // Upload your OWN edited clip → returns a URL you can drop into restitch in place of a clip.
  uploadEditedClip: (file: File, filename?: string) => {
    const fd = new FormData();
    fd.append('clip', file);
    if (filename) fd.append('filename', filename);
    return api.post('/regen/clips/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  refixClip: (variantId: string, index: number, body: { instruction: string; dry_run?: boolean; seconds?: number }) =>
    api.post(`/regen/variants/${variantId}/clips/${index}/refix`, body),

  feedbackOnly: (body: {
    request_id?: string; variant_id?: string; creative_ref?: string; feedback: string;
    persona?: string; category?: string; confirmed?: boolean; rule?: string;
    normalized?: boolean; context?: Record<string, any>;
  }) => api.post('/regen/feedback-only', body),
  createTree: (body: { creative_id: string; phases?: string[]; counts?: any; model?: string }) => api.post('/regen/tree', body),
  getTree: (id: string) => api.get(`/regen/tree/${id}`),
  listTrees: () => api.get('/regen/trees'),
  getAssets: (params?: { kind?: string; media?: string; vertical?: string; role?: string; usable_as?: string; age_band?: string; gender?: string; state_code?: string; q?: string; page?: number; page_size?: number }) => api.get('/regen/assets', { params }),
  // Auto Studio — one command → resolve character+script → voice → LatentSync (the CapCut flow)
  autoLipsync: (body: { command: string; asset_id?: string; voice_id?: string; script?: string; script_ref?: string; seconds?: number; clone_voice?: boolean; captions?: boolean; lipsync_provider?: string; quality?: string; confirm_voice?: boolean; script_mode?: string; request_type?: string; broll_asset_ids?: string }) => api.post('/regen/auto-lipsync', body),
  financeLedger: () => api.get('/regen/finance/ledger'),
  creationCosts: (request_id: string) => api.get('/regen/costs', { params: { request_id } }),
  providers: () => api.get('/regen/providers'),
  // A gallery mounts dozens of tiles at once; each /thumb makes the server download the video and
  // run ffmpeg. Firing them all in parallel is what produced the wall of
  // "AbortError: This operation was aborted" — they queue past their timeout. Cap concurrency.
  thumb: (params: { key?: string; url?: string }) => _thumbQueue(() => api.get('/regen/thumb', { params })),
  engineConfig: () => api.get('/regen/engine-config'),
  setEngineConfig: (body: { concurrency_cap?: number; monthly_budget_usd?: number; default_quality?: string }) => api.post('/regen/engine-config', body),
  orbitGenerate: (fileRequestId: string, body: { orbit_context?: string; seconds?: number; voice_id?: string; clone_voice?: boolean; asset_id?: string; captions?: boolean; dry_run?: boolean; preview?: boolean; selected_jobs?: number[]; variation_axis?: string; intent?: any; gen_path?: string }) => api.post(`/regen/file-requests/${fileRequestId}/orbit-generate`, body),
  // ORBIT conversational confirm (Phase 2): one turn of the Studio-style confirm loop, held as a DRAFT
  // (thread `orbit:<id>`, hidden from the sidebar). Never generates/spends. `intent`/`summary` = the
  // preview brain read, passed so the stated FLOW matches the preview.
  orbitChat: (fileRequestId: string, body: { message?: string; orbit_context?: string; confirm?: boolean; intent?: any; summary?: string }) => api.post(`/regen/file-requests/${fileRequestId}/orbit-chat`, body),
  // Loser-redraw conversational gate (Phase 2-4): the SAME confirm-loop pattern as orbitChat, retargeted
  // at a single losing creative's Phase-1 suggestion. Thread `redraw:<creative_id>`. Never spends until
  // a bare confirm ("go"/"yes") against an already-shown plan card, or an explicit `confirm:true`.
  redrawChat: (creativeId: string, body: { message?: string; confirm?: boolean }) => api.post(`/regen/creative/${creativeId}/redraw-chat`, body),
  listVoices: (params?: { gender?: string; age_band?: string }) => api.get('/regen/voices', { params }),
  // Creative Team "office" — live desk activity + per-persona performance reports.
  teamActivity: (jobId?: string) => api.get('/regen/creative-team/activity', { params: jobId ? { job_id: jobId } : {} }),

  // Team room → learning. Feedback aimed at ONE persona lands in that persona's own prompt
  // next job. `advisory: true` in the reply = that persona's plan was discarded for the job.
  // STEP 2: store it. `confirmed: true` means a human approved this exact wording, which is the
  // only way a lesson goes live immediately; anything else stays proposed and is never injected.
  teamFeedback: (body: {
    persona: string; note: string; job_id?: string; creative_ref?: string;
    reason?: string; style?: string; vertical?: string; category?: string;
    context?: Record<string, any>; raw_feedback?: string; normalized?: boolean; confirmed?: boolean;
  }) => api.post('/regen/creative-team/feedback', body),

  // STEP 1: interpret feedback and get it back for confirmation — writes nothing.
  teamFeedbackPropose: (body: {
    note: string; category?: string; persona?: string; job_id?: string;
    creative_ref?: string; style?: string; vertical?: string; context?: Record<string, any>;
  }) => api.post('/regen/creative-team/feedback/propose', body),

  teamLessonCategories: () => api.get('/regen/creative-team/lesson-categories'),

  // Manual vs automatic application + how much is awaiting review.
  teamLearningStatus: () => api.get('/regen/creative-team/learning-status'),

  teamLessonsAdmin: (params?: { persona?: string; status?: string; vertical?: string }) =>
    api.get('/regen/creative-team/lessons-admin', { params }),

  teamLessonStatus: (id: string, body: { status: 'active' | 'proposed' | 'retired'; rule?: string }) =>
    api.post(`/regen/creative-team/lessons/${id}/status`, body),

  teamLessonsBulkStatus: (ids: string[], status: 'active' | 'retired' | 'proposed') =>
    api.post('/regen/creative-team/lessons/bulk-status', { ids, status }),

  teamMeeting: (body: {
    job_id?: string; creative_ref?: string; agenda?: string; attendees?: string[];
    notes?: string; decisions?: Array<{ persona: string; rule: string; reason?: string }>;
  }) => api.post('/regen/creative-team/meeting', body),

  // SSE URL for the live room. EventSource can't set headers, so the token rides as a query
  // param — same pattern the rest of the app uses for direct-media URLs.
  teamStreamUrl: (jobId?: string) => {
    const base = (api.defaults.baseURL || '').replace(/\/$/, '');
    const tok = getAccessToken() || '';
    const qs = new URLSearchParams();
    if (jobId) qs.set('job_id', jobId);
    if (tok) qs.set('access_token', tok);
    return `${base}/regen/creative-team/stream${qs.toString() ? `?${qs}` : ''}`;
  },
  // Several components watch the office at once (monitor widget + team room). Without this they
  // each poll independently and blow the rate limit — that was the 429 on /creative-team/jobs.
  // One in-flight request, shared by every caller, with a short cache.
  teamJobs: () => {
    const now = Date.now();
    if (_jobsCache.promise && now - _jobsCache.at < 4000) return _jobsCache.promise;
    _jobsCache.at = now;
    _jobsCache.promise = api.get('/regen/creative-team/jobs')
      .catch((e: any) => { _jobsCache.at = 0; throw e; });   // let a failure re-fetch immediately
    return _jobsCache.promise;
  },
  enhancePrompt: (body: { prompt: string; mode: 'enhance' | 'script'; engine?: string; vertical?: string; seconds?: number }) => api.post('/regen/creative-team/enhance', body),
  teamReports: () => api.get('/regen/creative-team/reports'),
  teamAudit: (jobId: string, persona?: string) => api.get('/regen/creative-team/audit', { params: { job_id: jobId, ...(persona ? { persona } : {}) } }),
  teamDecision: (jobId: string) => api.get('/regen/creative-team/decision', { params: { job_id: jobId } }),
  // Create from Assets — scenic images + script → end-to-end narrated video.
  fromAssets: (form: FormData) => api.post('/regen/from-assets', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Storyboard board — character-reference image + script + scene count → plan + cost preview →
  // per-scene stills (each a checkbox thumbnail) → regenerate only the CHECKED panels (others stay
  // locked) → render the final video. preview is multipart (carries the reference image); the returned
  // `spec` bakes in the uploaded image URL, so stills/regenerate/render pass it back as plain JSON.
  storyboardPreview: (form: FormData) => api.post('/regen/storyboard/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  storyboardStills: (body: { spec: any }) => api.post('/regen/storyboard/stills', body),
  storyboardRegenerate: (body: { spec: any; scenes: any[]; panels: number[] }) => api.post('/regen/storyboard/regenerate', body),
  storyboardRender: (body: { spec: any; scenes: any[] }) => api.post('/regen/storyboard/render', body),
  // Saved storyboard stills — grouped by session (newest first), images ordered by scene. Read-only gallery.
  storyboardImages: () => api.get('/regen/storyboard/images'),
  // Audio-only deliverable — script + chosen voice → a delivered voiceover mp3 (backend `wantsAudio`).
  generateAudio: (body: { script: string; voice_id?: string; clone_voice?: boolean; vertical?: string; dry_run?: boolean }) => api.post('/regen/generate-audio', body),
  // Audio-upload lipsync — upload an audio file + a video OR image → a lipsynced video (multipart).
  lipsyncUpload: (form: FormData) => api.post('/regen/lipsync-upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Studio generation console (prompt + reference image(s) → video; Seedance or Veo extend).
  generate: (form: FormData) => api.post('/regen/generate', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  generations: (params?: { limit?: number; offset?: number; from?: string; to?: string }) => api.get('/regen/generations', { params }),
  // REVIEW-FIRST: combine approved clips → final video; regenerate only selected clips with a reason.
  stitchClips: (id: string) => api.post(`/regen/requests/${id}/stitch`, {}),
  regenClips: (id: string, regen: { index: number; reason: string }[]) => api.post(`/regen/requests/${id}/regen-clips`, { regen }),
  // Upload an external video file straight into ORBIT Generated (multipart). Optional owner_email files it
  // under that user; label/filename are shown on the card. Returns { request_id, video_url, s3_key }.
  uploadVideo: (file: File, opts?: { label?: string; owner_email?: string }) => {
    const fd = new FormData();
    fd.append('clip', file);
    if (opts?.label) fd.append('label', opts.label);
    if (file.name) fd.append('filename', file.name);
    if (opts?.owner_email) fd.append('owner_email', opts.owner_email);
    return api.post('/regen/ingest-clip', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // #12 reconcile/auto-editor — re-stitch a delivered video's raw clips (drop/reorder) into a new variant (no re-render).
  restitchVariant: (id: string, clipUrls: string[]) => api.post(`/regen/variants/${id}/restitch`, { clip_urls: clipUrls }),
  // #8 manual editor — cut the source into ordered [start,end] windows and restitch ($0). Powers the
  // timeline editor's trim/delete/reorder/split. dry_run:true previews the plan + result duration.
  editTimeline: (body: { source_request_id?: string; source_url?: string; segments: { start: number; end: number | null }[]; transition?: string; dry_run?: boolean }) =>
    api.post('/regen/studio/edit-timeline', body),
  // "Ask AI to cut & replace" — swap one window [start,end] for a generated/supplied clip and restitch.
  segmentReplace: (body: { source_request_id?: string; source_url?: string; start: number; end: number; replacement_prompt?: string; replacement_url?: string; keep_audio?: boolean; dry_run?: boolean }) =>
    api.post('/regen/studio/segment-replace', body),
  // Burn text/caption layers onto a video ($0 ffmpeg).
  burnText: (body: { source_request_id?: string; source_url?: string; texts: { text: string; x: number; y: number; size?: number; color?: string; start?: number | null; end?: number | null }[]; dry_run?: boolean }) =>
    api.post('/regen/studio/burn-text', body),
  // Add a music/VO track to a video ($0 ffmpeg) — mix under the original or replace it.
  addAudio: (body: { source_request_id?: string; source_url?: string; audio_url: string; keep_original?: boolean; music_volume?: number; dry_run?: boolean }) =>
    api.post('/regen/studio/add-audio', body),
  // Studio thread memory — ChatGPT-style: one chat turn → artifacts (scripts/ad-copy/video/image).
  studioChat: (body: { message: string; thread_id?: string; engine?: string; vertical?: string; seconds?: number; aspect_ratio?: string; resolution?: string; generate_audio?: boolean; captions?: boolean; allow_rewrite?: boolean; gen_path?: string; seedance_model?: string; dry_run?: boolean; review_only?: boolean; request_type?: string; brief?: string; do_not_say?: string[]; recreate_source_url?: string; recreate_label?: string; recreate_thumb?: string }) => api.post('/regen/studio/chat', body),
  // Same chat turn, but multipart so a reference image can be attached (field `images`) → image-to-video.
  studioChatForm: (form: FormData) => api.post('/regen/studio/chat', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Redeem an Ad Library "Recreate in Studio" link. Single-use on the library's side, so call
  // it exactly once per hand-off — a replay answers already_open, not an error.
  studioHandoff: (token: string) => api.get(`/regen/studio/handoff/${encodeURIComponent(token)}`),
  studioThread: (thread_id?: string) => api.get('/regen/studio/thread', { params: thread_id ? { thread_id } : {} }),
  // Multi-conversation Studio: list the user's past chats (newest first) for the sidebar.
  studioThreads: () => api.get('/regen/studio/threads'),
  // Rename / delete a single Studio conversation (Claude-Code-style thread management).
  studioThreadRename: (thread_id: string, title: string) => api.patch(`/regen/studio/thread/${encodeURIComponent(thread_id)}`, { title }),
  studioThreadDelete: (thread_id: string) => api.delete(`/regen/studio/thread/${encodeURIComponent(thread_id)}`),
  // Self-correcting learning loop — proposals, brains, decision audit, changelog.
  learnProposals: (params?: { status?: string; brain?: string; vertical?: string; limit?: number }) => api.get('/regen/learn/proposals', { params }),
  learnApprove: (id: string) => api.post(`/regen/learn/proposals/${encodeURIComponent(id)}/approve`, {}),
  learnReject: (id: string, reason: string) => api.post(`/regen/learn/proposals/${encodeURIComponent(id)}/reject`, { reason }),
  learnBrains: (vertical?: string) => api.get('/regen/learn/brains', { params: vertical ? { vertical } : {} }),
  learnDecisions: (request_id: string) => api.get(`/regen/learn/decisions/${encodeURIComponent(request_id)}`),
  learnEvents: (params?: { brain?: string; vertical?: string; limit?: number }) => api.get('/regen/learn/events', { params }),
  learnRunTuning: () => api.post('/regen/learn/run-tuning', {}),
  // SPEED EXPORT — re-time a FINISHED variant at a fine step (ffmpeg on the delivered mp4; no render/spend).
  // Returns the re-timed mp4 as a blob for the browser to download.
  exportSpeed: (variantId: string, speed: number) =>
    api.post(`/regen/variants/${encodeURIComponent(variantId)}/export-speed`, { speed }, { responseType: 'blob', timeout: 300000 }),
};

// Winners Library (admin/ceo only) — paginated gallery of proven winning creatives + detail/playback.
export const winnersApi = {
  list: (params?: { vertical?: string; type?: string; search?: string; page?: number; limit?: number; sort?: 'profit' | 'roi' | 'conversions' }) =>
    api.get('/winners', { params }),
  get: (id: string) => api.get(`/winners/${encodeURIComponent(id)}`),
  types: (vertical?: string) => api.get('/winners/types', { params: { vertical } }),
};

export default api;
